import { motion } from "framer-motion";
import { Cloud, Server, ShieldCheck, Check, X, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { lenisRef } from "@/lib/lenis";
import { useI18n } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];

const C = {
  ro: {
    label: "Comparație",
    h1: "Cloud public vs.",
    h2: "infrastructura ta.",
    sub: "Aceleași aplicații, două lumi diferite. Iată, pe scurt, de ce contează unde stau de fapt datele tale.",
    colA: "Cloud public",
    colAsub: "închiriezi de la un furnizor",
    colB: "Infrastructura ta",
    colBsub: "construită de noi, deținută de tine",
    badge: "Recomandat",
    rows: [
      { k: "Unde stau datele", a: "Pe servere străine, undeva în lume", b: "Fizic la tine, pe serverul tău" },
      { k: "Cine deține controlul", a: "Furnizorul — tu doar închiriezi", b: "Tu, 100%. Acces fizic total" },
      { k: "Confidențialitate & GDPR", a: "Datele trec prin terți, greu de auditat", b: "Date private, conforme, sub cheia ta" },
      { k: "Risc de securitate", a: "Țintă mare, expusă public non-stop", b: "Suprafață minimă, izolată de internetul public" },
      { k: "Cost în timp", a: "Abonament lunar care crește la nesfârșit", b: "Investiție o dată — o deții pe termen lung" },
      { k: "Dacă pică internetul", a: "Nu mai ai acces la nimic", b: "Merge local, în rețeaua ta" },
      { k: "Dependență (lock-in)", a: "Prins în ecosistemul lor", b: "Zero vendor lock-in — ești liber" },
    ],
    cta: "Vrei datele tale la tine? Hai să discutăm",
  },
  en: {
    label: "Comparison",
    h1: "Public cloud vs.",
    h2: "your own infrastructure.",
    sub: "Same apps, two different worlds. Here's, in short, why it matters where your data actually lives.",
    colA: "Public cloud",
    colAsub: "you rent from a provider",
    colB: "Your infrastructure",
    colBsub: "built by us, owned by you",
    badge: "Recommended",
    rows: [
      { k: "Where the data lives", a: "On foreign servers, somewhere in the world", b: "Physically with you, on your own server" },
      { k: "Who's in control", a: "The provider — you only rent", b: "You, 100%. Full physical access" },
      { k: "Privacy & GDPR", a: "Data flows through third parties, hard to audit", b: "Private, compliant data, under your key" },
      { k: "Security risk", a: "A big target, publicly exposed 24/7", b: "Minimal surface, isolated from the public internet" },
      { k: "Cost over time", a: "A monthly subscription that grows forever", b: "One-time investment — you own it long term" },
      { k: "If the internet goes down", a: "You lose access to everything", b: "Runs locally, on your own network" },
      { k: "Dependency (lock-in)", a: "Trapped in their ecosystem", b: "Zero vendor lock-in — you're free" },
    ],
    cta: "Want your data with you? Let's talk",
  },
};

export const CloudCompare = () => {
  const { lang } = useI18n();
  const d = C[lang === "en" ? "en" : "ro"];

  const goContact = () => {
    const lenis = lenisRef.current;
    if (lenis) lenis.scrollTo("#contact", { offset: -60, duration: 1.2 });
    else document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="comparatie"
      data-testid="cloud-compare-section"
      className="relative overflow-hidden border-t border-white/10 bg-ink-800"
    >
      <div className="pointer-events-none absolute right-0 top-0 h-[26rem] w-[26rem] rounded-full bg-cyan-neon/10 blur-[150px]" />

      <div className="relative mx-auto max-w-6xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="max-w-3xl">
          <Reveal><SectionLabel index="[ COMPARAȚIE ]">{d.label}</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {d.h1} <span className="text-aurora text-aurora-glow">{d.h2}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl font-body text-base leading-relaxed text-white/60">{d.sub}</p>
          </Reveal>
        </div>

        {/* Table */}
        <Reveal delay={0.1}>
          <div
            data-testid="compare-table"
            className="mt-12 overflow-hidden rounded-2xl border border-white/10 bg-ink-900"
          >
            {/* Header (desktop) */}
            <div className="hidden md:grid md:grid-cols-[1.15fr_1fr_1fr]">
              <div className="p-5" />
              <div className="border-l border-white/10 p-5">
                <div className="flex items-center gap-2 font-display text-base font-bold text-white/70">
                  <Cloud size={18} className="text-white/40" /> {d.colA}
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/35">{d.colAsub}</div>
              </div>
              <div className="relative border-l border-cyan-neon/30 bg-cyan-neon/[0.06] p-5">
                <span className="absolute right-4 top-4 rounded-full border border-cyan-neon/40 bg-cyan-neon/15 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-cyan-neon">
                  {d.badge}
                </span>
                <div className="flex items-center gap-2 font-display text-base font-bold text-white">
                  <ShieldCheck size={18} className="text-cyan-neon" /> {d.colB}
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-cyan-neon/70">{d.colBsub}</div>
              </div>
            </div>

            {/* Rows */}
            <div className="divide-y divide-white/10">
              {d.rows.map((r, i) => (
                <motion.div
                  key={r.k}
                  data-testid={`compare-row-${i}`}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, ease: EASE, delay: (i % 7) * 0.04 }}
                  className="grid grid-cols-1 md:grid-cols-[1.15fr_1fr_1fr]"
                >
                  {/* criterion */}
                  <div className="px-5 pb-1 pt-5 font-display text-sm font-bold text-white md:py-5 md:text-white/90">
                    {r.k}
                  </div>

                  <div className="grid grid-cols-1 gap-px sm:grid-cols-2 md:contents">
                    {/* cloud */}
                    <div className="flex items-start gap-2.5 border-l border-white/10 px-5 py-4 md:py-5">
                      <X size={16} className="mt-0.5 shrink-0 text-red-400/70" />
                      <div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/30 md:hidden">{d.colA}</div>
                        <div className="font-body text-sm leading-relaxed text-white/45">{r.a}</div>
                      </div>
                    </div>
                    {/* infra ta */}
                    <div className="flex items-start gap-2.5 border-l border-cyan-neon/30 bg-cyan-neon/[0.06] px-5 py-4 md:py-5">
                      <Check size={16} className="mt-0.5 shrink-0 text-cyan-neon" />
                      <div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-cyan-neon/60 md:hidden">{d.colB}</div>
                        <div className="font-body text-sm font-medium leading-relaxed text-white/85">{r.b}</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 flex justify-center">
            <button
              data-testid="compare-cta"
              onClick={goContact}
              className="btn-shine group inline-flex items-center gap-3 bg-cyan-neon px-7 py-4 font-mono text-sm font-bold uppercase tracking-[0.1em] text-black shadow-[0_0_30px_rgba(0,229,255,0.3)] transition-colors duration-300 hover:bg-white"
            >
              {d.cta}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
